// routes/config/tournamentRoutes.ts
/**
 * Tournament Route Configuration
 * Routes for tournament management and operations
 */

import { lazy } from 'react';
import { ROUTES } from '../../config/routes';
import type { RouteConfig } from '../types';

const TournamentSetupPage = lazy(() => import('../../features/tournaments/pages/TournamentSetupPage'));
const ArchivedTournamentsPage = lazy(() => import('../../features/tournaments/pages/ArchivedTournamentsPage'));
const TournamentRunnerPage = lazy(() => import('../../features/tournament-engine/pages/TournamentRunnerPage'));
const MatchmakingRunnerPage = lazy(() => import('../../features/matchmaking-runner/pages/MatchmakingRunnerPage'));
const MatchmakingDashboardPage = lazy(() => import('../../features/matchmaking-runner/pages/MatchmakingDashboardPage'));
const DQCasesPage = lazy(() => import('../../features/dq-cases/pages/DQCasesPage'));
const DQCaseCreatePage = lazy(() => import('../../features/dq-cases/pages/DQCaseCreatePage'));
const DQCaseDetailPage = lazy(() => import('../../features/dq-cases/pages/DQCaseDetailPage'));
const StreamingTopCutPage = lazy(() => import('../../features/streaming/pages/StreamingTopCutPage'));
const StoreManagersPage = lazy(() => import('../../features/store-managers/pages/StoreManagersPage'));
const StoreInviteClaimPage = lazy(() => import('../../features/store-managers/pages/StoreInviteClaimPage'));
const TournamentSeriesPage = lazy(() => import('../../features/tournament-series/pages/TournamentSeriesPage'));
const TournamentSeriesCreatePage = lazy(() => import('../../features/tournament-series/pages/TournamentSeriesCreatePage'));
const TournamentSeriesDetailPage = lazy(() => import('../../features/tournament-series/pages/TournamentSeriesDetailPage'));
const SeriesInvitePage = lazy(() => import('../../features/tournament-series/pages/SeriesInvitePage'));
const TableManagementPage = lazy(() => import('../../features/tables/pages/TableManagementPage'));
const ViewPlayersPage = lazy(() => import('../../features/players/pages/ViewPlayersPage'));
const PlayerUploadPage = lazy(() => import('../../features/players/pages/PlayerUploadPage'));
const DeckCheckPage = lazy(() => import('../../features/deckcheck/pages/DeckCheckPage'));
const AppealsPage = lazy(() => import('../../features/appeals/pages/AppealsPage'));
const AppealsLogPage = lazy(() => import('../../features/appeals/pages/AppealsLogPage'));
const PenaltyApprovalPage = lazy(() => import('../../features/penalties/pages/PenaltyApprovalPage'));
const TimerOverviewPage = lazy(() => import('../../features/timer/pages/TimerOverviewPage'));
const RoundArchivePage = lazy(() => import('../../features/rounds/pages/RoundArchivePage'));
const ProblemTrackerPage = lazy(() => import('../../features/scorekeeper/pages/ProblemTrackerPage'));

export const tournamentRoutes: RouteConfig[] = [
  // Tournament Setup. accountType 'staff' is intentionally
  // included so store-managers (proxy accounts) can reach the
  // page — the page's own canCreateTournament check then rejects
  // staff users who don't actually have storeManagerships +
  // canCreateAndEditTournaments. The route is the broad gate;
  // the page is the narrow gate.
  {
    path: ROUTES.TOURNAMENT_SETUP,
    element: TournamentSetupPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Scorekeeper'],
    accountTypes: ['admin', 'store', 'staff'],
  },
  {
    path: ROUTES.TOURNAMENT_SETUP_EDIT,
    element: TournamentSetupPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Scorekeeper'],
    accountTypes: ['admin', 'store', 'staff'],
  },
  // Archived tournaments. 'staff' included for store-manager
  // proxies — the page itself filters to archives the user owns
  // or proxies for, and rejects staff users without any
  // storeManagership.
  {
    path: ROUTES.ARCHIVED_TOURNAMENTS,
    element: ArchivedTournamentsPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store', 'staff'],
  },

  // Tournament Engine
  {
    path: ROUTES.TOURNAMENT_RUNNER,
    element: TournamentRunnerPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['store'],
  },
  {
    path: ROUTES.TOURNAMENT_STANDINGS,
    element: TournamentRunnerPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['store'],
    props: { defaultTab: 'standings' },
  },
  {
    path: ROUTES.TOURNAMENT_BRACKET,
    element: TournamentRunnerPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['store'],
    props: { defaultTab: 'bracket' },
  },

  // Matchmaking Runner (on-demand queue-based sessions)
  {
    path: ROUTES.MATCHMAKING_RUNNER,
    element: MatchmakingRunnerPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.MATCHMAKING_DASHBOARD,
    element: MatchmakingDashboardPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['admin', 'store', 'staff'],
  },

  // DQ Cases - list visible to admin/store and any tournament-staff
  // role (assignment-scoped at the DB level). Create gated to
  // Manager / store / admin.
  {
    path: ROUTES.DQ_CASES,
    element: DQCasesPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
    accountTypes: ['admin', 'store', 'staff'],
  },
  {
    path: ROUTES.DQ_CASE_NEW,
    element: DQCaseCreatePage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store'],
  },
  {
    path: ROUTES.DQ_CASE_DETAIL,
    element: DQCaseDetailPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
    accountTypes: ['admin', 'store', 'staff'],
  },

  // Streaming top-cut helper page. Reads the same two Bandai
  // CSVs as the Top Cut Export tab and produces the broadcast-
  // format CSV (Slot, TCG+, Name, Score, Leader, Color 1,
  // Color 2). Scraper backend ships as a follow-up Cloud
  // Function — the page renders the table and accepts manual
  // leader fill-in until that's wired up.
  {
    path: ROUTES.STREAMING_TOP_CUT,
    element: StreamingTopCutPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['admin', 'store'],
  },

  // Store Managers — store owners delegate event management to
  // trusted staff. Admins can open this page too (for support /
  // store-on-behalf-of view via the picker).
  {
    path: ROUTES.STORE_MANAGERS,
    element: StoreManagersPage,
    auth: 'protected',
    roles: ['admin'],
    accountTypes: ['admin', 'store'],
  },
  // Single-use invite-claim landing. Any authenticated user can
  // load this — the token is the credential. Auth gate uses
  // 'optional' so the page itself can show a 'sign in first'
  // state if needed (the page handles its own auth branching).
  {
    path: ROUTES.STORE_INVITE_CLAIM,
    element: StoreInviteClaimPage,
    auth: 'optional',
  },

  // Tournament Series - admin/store can manage series; Manager can
  // open the detail page when attaching a tournament. 'staff'
  // included so store-manager proxies with canManageSeries can
  // reach the list; the page filters series to the stores the
  // user owns/proxies for.
  {
    path: ROUTES.TOURNAMENT_SERIES,
    element: TournamentSeriesPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store', 'staff'],
  },
  {
    path: ROUTES.TOURNAMENT_SERIES_NEW,
    element: TournamentSeriesCreatePage,
    auth: 'protected',
    roles: ['admin'],
    // 'staff' included for store-manager proxies; the page's own
    // permission check rejects staff users without the right
    // storeManagership.
    accountTypes: ['admin', 'store', 'staff'],
  },
  {
    path: ROUTES.TOURNAMENT_SERIES_DETAIL,
    element: TournamentSeriesDetailPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['admin', 'store', 'staff'],
  },
  // Series invite magic link - any authenticated user can accept,
  // since the series staff pool is open to anyone the manager invites.
  {
    path: ROUTES.SERIES_INVITE,
    element: SeriesInvitePage,
    auth: 'protected',
    roles: [],
  },

  // Table Management
  {
    path: ROUTES.TABLE_MANAGEMENT,
    element: TableManagementPage,
    auth: 'protected',
    roles: [], // All authenticated users
  },

  // Player Management
  {
    path: ROUTES.VIEW_PLAYERS,
    element: ViewPlayersPage,
    auth: 'protected',
    roles: [],
  },
  {
    path: ROUTES.PLAYER_UPLOAD,
    element: PlayerUploadPage,
    auth: 'protected',
    roles: ['admin', 'Manager'],
    accountTypes: ['store'],
  },

  // Deck Check
  {
    path: ROUTES.DECK_CHECK,
    element: DeckCheckPage,
    auth: 'protected',
    roles: [],
  },

  // Appeals
  {
    path: ROUTES.APPEALS,
    element: AppealsPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge'],
    accountTypes: ['store'],
    allowHeadJudgeTeam: true,
  },
  {
    path: ROUTES.APPEALS_LOG,
    element: AppealsLogPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge'],
    accountTypes: ['store'],
    allowHeadJudgeTeam: true,
  },

  // Penalties
  {
    path: ROUTES.PENALTY_APPROVAL,
    element: PenaltyApprovalPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Team Lead', 'Floor Judge'],
    accountTypes: ['store'],
    allowHeadJudgeTeam: true,
    allowActingLead: true,
  },

  // Timer
  {
    path: ROUTES.TIMER_OVERVIEW,
    element: TimerOverviewPage,
    auth: 'protected',
    roles: [],
  },

  // Rounds
  {
    path: ROUTES.ROUND_ARCHIVE,
    element: RoundArchivePage,
    auth: 'protected',
    roles: [],
  },

  // Problem Tracker
  {
    path: ROUTES.PROBLEM_TRACKER,
    element: ProblemTrackerPage,
    auth: 'protected',
    roles: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
    accountTypes: ['admin', 'store', 'staff'],
    allowHeadJudgeTeam: true,
  },
];
